import { createContext, useContext, useMemo } from 'react';

import { usePromptContext } from '@/store/prompts';
import type { PromptContextSubject } from '@/types/prompts';
import { CONTEXT_MARKERS } from '@/utils/promptContext';

import type { FillContext } from './promptFill';

/**
 * What a chat is about, for Context Variables (spec FSI #1230, VH3_CONNECT
 * #632). `PromptSubjectProvider` runs the lookup for a chat's subject and
 * shares it; the composer's menus and fill step read it here instead of
 * taking props through every layer.
 */

export type PromptSubjectValue = {
  subject: PromptContextSubject | null;
  context: FillContext;
  /** The lookup is not in flight: it answered, failed, or never ran. */
  settled: boolean;
};

export const PromptSubjectContext = createContext<PromptSubjectValue | null>(
  null
);

export const toSubjectValue = (
  subject: PromptContextSubject | null,
  response: { names: FillContext['names']; values: FillContext['values'] },
  fetching: boolean,
  failed: boolean
): PromptSubjectValue => ({
  subject,
  context: {
    names: response.names,
    values: response.values,
    marker: subject ? CONTEXT_MARKERS[subject.kind] : undefined,
    // No list yet counts as a failed lookup: a prompt is never inserted with
    // a `{name}` left in, so the fill step opens with empty fields and the
    // late answer fills them. With the switch off nothing is fetching.
    failed: failed || (fetching && response.names.length === 0),
  },
  settled: !fetching,
});

/**
 * The chat's subject, its list of Context Variables and the values found.
 * Without a provider the chat has no subject, so only the Everywhere values
 * are found (the lookup is cached, so it runs once for the whole app).
 */
export function usePromptSubject(): PromptSubjectValue {
  const provided = useContext(PromptSubjectContext);
  const { context, isFetching, isError } = usePromptContext(
    null,
    provided === null
  );
  const fallback = useMemo(
    () => toSubjectValue(null, context, isFetching, isError),
    [context, isFetching, isError]
  );
  return provided ?? fallback;
}

/** The context the fill step and the menus read. */
export const usePromptFillContext = (): FillContext =>
  usePromptSubject().context;
